/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import capitalize from 'lodash/capitalize';
import merge from 'lodash/merge';
import { compilerService } from 'amb/services/compiler.service';
import { objectHelper } from 'amb/helpers/object.helper';
import { optionsHelper } from 'amb/helpers/options.helper';
import { intersectionDirective } from './intersection.directive';
import { providers } from '../models/video/providers';
import { videoOptions } from '../options/video.options';

/**
 * Video directive
 *
 * @typedef {import('../models/video/abstract-video').AbstractVideo} AbstractVideo
 * @typedef {typeof import('../models/video/abstract-video').AbstractVideo} AbstractVideoType
 */
export const videoDirective = {
  /**
   * Video options container
   *
   * @protected
   */
  options: videoOptions,

  /**
   * @type {Map<string, AbstractVideo>}
   */
  videos: new Map(),

  /**
   * @protected
   * @type {Map<string, MediaQueryList[]}
   */
  responsiveQueries: new Map(),

  /**
   * @protected
   * @type {Map<string, MediaQueryList[]}
   */
  srcsetQueries: new Map(),

  /**
   * Initialize video for the given element or elements which match the selector
   *
   * @param {HTMLElement} element HTML DOM element
   * @param {object}      options Video options
   */
  init(element, options = {}) {
    const input = (element.getAttribute('amb-video') || element.dataset.ambVideo).replace(/\?.*/, '');
    const { id, VideoModel } = this.parse(input);

    if (!VideoModel) {
      console.warn(`The video with input "${input}" is not supported!`);
    } else if (!id) {
      console.warn(`The configured ${capitalize(VideoModel.TYPE)} video is not valid.`);
    } else {
      this.handleReadonly(element, options);
      this.handleAutopilot(element, options);
      this.handleMediaQuery(element, options, input);

      options.defaultVideoId = id;
      const video = new VideoModel(element, options);

      this.videos.set(element.ambid, video);
    }
  },

  /**
   * Destroy plugin for the given element
   *
   * @param {HTMLElement} element HTML DOM element
   */
  destroy(element) {
    const ambid = element.ambid;

    if (this.videos.has(ambid)) {
      this.videos.get(ambid).destroy();
      this.videos.delete(ambid);
    }

    if (this.responsiveQueries.has(ambid)) {
      const mediaQueries = this.responsiveQueries.get(ambid);

      this.unregisterMediaQueries(mediaQueries);
      this.responsiveQueries.delete(ambid);
    }

    if (this.srcsetQueries.has(ambid)) {
      const mediaQueries = this.srcsetQueries.get(ambid);

      this.unregisterMediaQueries(mediaQueries);
      this.srcsetQueries.delete(ambid);
    }

    $(element).off();

    delete element.player;
  },

  /**
   * Parse the configured video and return the video ID and Model
   *
   * @protected
   *
   * @param {string} input The configured video input string
   *
   * @returns {{id: string, VideoModel: AbstractVideoType}}
   */
  parse(input) {
    let id;
    const VideoModel = providers.find(provider => provider.TYPE_PATTERN.test(input));

    if (VideoModel) {
      const match = input.match(VideoModel.ID_PATTERN);

      id = match && match[1];
    }

    return { id, VideoModel };
  },

  /**
   * Handle readonly videos
   *
   *  - If video element contains `readonly` attribute, the video will enable `readonly` mode as well.
   *  - On the other hand, if `readonly` option enabled, `readonly` attribute will add to video element as well.
   *  - When enabled, `autopilot` mode will force to enable as well to control the video playback.
   *
   * @protected
   *
   * @param {HTMLElement} element HTML DOM element
   * @param {object}      options Video options
   */
  handleReadonly(element, options) {
    options.readonly = options.readonly || element.hasAttribute('readonly');

    if (options.readonly) {
      if (!element.hasAttribute('readonly')) {
        element.setAttribute('readonly', '');
      }

      // force to enable `autopilot` mode for videos rendered as `readonly` content.
      if (options.autopilot === undefined) {
        options.autopilot = true;
      }

      // Inherit options from `readonly` preset.
      Object.assign(options, this.options.readonly);
    }
  },

  /**
   * Enable `autopilot` mode, register and compile `amb-intersection` directive.
   *
   * @protected
   *
   * @param {HTMLElement} element HTML DOM element
   * @param {object}      options Video options
   */
  handleAutopilot(element, options) {
    if (!options.autopilot) {
      // Autopilot disabed, do nothing...
    } else if (compilerService.directives.has('ambIntersection')) {
      // Inherit options from `autopilot` preset.
      Object.assign(options, this.options.autopilot);

      // If no `loop` option configured, we'll set it to `true` for backward compatible
      if (options.loop === undefined) {
        options.loop = true;
      }

      // `autoplay` flag should be `false` as video playback will controlled programmatically by other directive.
      if (options.autoplay === undefined) {
        options.autoplay = false;
      }

      if (options.mute === undefined) {
        options.mute = true;
      }

      if (options.muted === undefined) {
        options.muted = true;
      }

      if (!element.hasAttribute('amb-intersection')) {
        element.setAttribute('amb-intersection', typeof options.autopilot === 'string' ? options.autopilot : 'video');

        compilerService.compileElementByAttribute(element, element.getAttributeNode('amb-intersection'));
      } else if (typeof options.autopilot === 'string'
        && element.getAttribute('amb-intersection') !== options.autopilot) {
        intersectionDirective.destroy(element);
        element.setAttribute('amb-intersection', options.autopilot);
        compilerService.compileElementByAttribute(element, element.getAttributeNode('amb-intersection'));
      }
    } else {
      console.warn('Unable control video programmatically without `amb-intersection` directive.');
    }
  },

  /**
   * Handle user configured media queries, include: `responsive` and `srcset`
   *
   * @protected
   *
   * @param {HTMLElement} element HTML DOM element
   * @param {object}      options Video options
   * @param {string}      input   The raw input value from directive attribute
   */
  handleMediaQuery(element, options, input) {
    let matchedMediaVideo;

    if (optionsHelper.isValidSrcset(input)) {
      matchedMediaVideo = this.registerSrcset(element, optionsHelper.parseSrcset(input));
    } else if (options.srcset) {
      matchedMediaVideo = this.registerSrcset(element, optionsHelper.parseSrcset(options.srcset));
    }

    if (matchedMediaVideo) {
      options.id = matchedMediaVideo;
    }

    options.responsiveOptions = this.registerResponsive(element, options.responsive);
  },

  /**
   * Register media query event for configured `srcset`
   *
   * @protected
   *
   * @param {HTMLElement} element HTML DOM element
   * @param {object}      srcset  Object contains user configured srcset
   *
   * @returns {string} Video ID which match with current device
   */
  registerSrcset(element, srcset) {
    const listener = this.onSrcsetChange.bind(this);
    const mediaQueries = this.registerMediaQuery(element.ambid, srcset, listener);
    const matchVideoId = optionsHelper.getMatchedMediaValue(mediaQueries);

    this.srcsetQueries.set(element.ambid, mediaQueries);

    return matchVideoId;
  },

  /**
   * Register media query event for configured `responsive` options
   *
   * @protected
   *
   * @param {HTMLElement} element    HTML DOM element
   * @param {object}      responsive Configured responsive options
   *
   * @returns {object} Responsive options which match with current device
   */
  registerResponsive(element, responsive) {
    const listener = this.onResponsiveOptionsChange.bind(this);
    const mediaQueries = this.registerMediaQuery(element.ambid, optionsHelper.fixResponsiveOptions(responsive), listener);
    const responsiveOptions = optionsHelper.getMatchedMediaValue(mediaQueries);

    this.responsiveQueries.set(element.ambid, mediaQueries);

    return responsiveOptions;
  },

  /**
   * Convert and register media queries
   *
   * @protected
   *
   * @param {string}   ambid    Unique element ID
   * @param {object}   queries  Object contains media query as key
   * @param {function} listener Listener which will execute when current device match the query
   *
   * @returns {MediaQueryList[]}
   */
  registerMediaQuery(ambid, queries, listener) {
    const mediaQueries = [];

    for (const query in queries) {
      const mediaQuery = window.matchMedia(query);

      mediaQuery.ambid = ambid;
      mediaQuery.listener = listener;
      mediaQuery.value = queries[query];
      mediaQueries.push(mediaQuery);

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', listener, { passive: true, capture: false });
      } else {
        mediaQuery.addListener(listener);
      }
    }

    return mediaQueries;
  },

  /**
   * Unregister media query
   *
   * @protected
   *
   * @param {MediaQueryList[]} mediaQueries List of registered media queries
   */
  unregisterMediaQueries(mediaQueries) {
    mediaQueries.forEach(mediaQuery => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', mediaQuery.listener);
      } else {
        mediaQuery.removeListener(mediaQuery.listener);
      }

      delete mediaQuery.ambid;
      delete mediaQuery.listener;
      delete mediaQuery.value;
    });
  },

  /**
   * Fires when registered breakpoints changes
   *
   * @protected
   *
   * @param {MediaQueryListEvent} event Media query change event
   */
  onSrcsetChange(event) {
    const ambid = event.target.ambid;
    const video = this.videos.get(ambid);

    if (video && !video.isDestroyed()) {
      const mediaQueries = this.srcsetQueries.get(ambid);
      const matchVideoId = optionsHelper.getMatchedMediaValue(mediaQueries);

      video.loadVideo(matchVideoId || video.defaultVideoId);
    }
  },

  /**
   * Fires when registered breakpoints changes
   *
   * @protected
   *
   * @param {MediaQueryListEvent} event Media query change event
   */
  onResponsiveOptionsChange(event) {
    const ambid = event.target.ambid;
    const video = this.videos.get(ambid);

    if (video && !video.isDestroyed()) {
      const mediaQueries = this.responsiveQueries.get(ambid);
      const responsiveOptions = optionsHelper.getMatchedMediaValue(mediaQueries);
      const newOptions = merge({}, video.originOptions, responsiveOptions);
      const diffOptions = objectHelper.difference(newOptions, video.options);

      video.setOptions(diffOptions);
    }
  }
};
